/**
 * Debounced auto-save for the "When evaluating" output-cleanup flags
 * (persisted via `save_evaluation_normalization`). Mirrors the debounce /
 * flush / unmount behavior of `useStudentOverridesAutoSave`.
 */

import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { GlobalContext } from '@/app/providers/globalState';
import { data_from_server_post } from '@/shared/http/client';
import {
    evaluationNormalizationEqual,
    evaluationNormalizationFromUsecase,
    evaluationNormalizationToPayload,
} from '@/features/model-setup/utils/evaluationNormalization';
import { AUTOSAVE_DEBOUNCE_MS } from './useTeacherPromptAutoSave';

const saveEvaluationNormalization = ({ usecase, normalization }) =>
    new Promise((resolve, reject) => {
        const payload = evaluationNormalizationToPayload(normalization);
        data_from_server_post(
            'save_evaluation_normalization',
            {
                client_id: usecase.client_id,
                llm_usecase_id: usecase._id,
                ...payload,
            },
            (result) => {
                if (result && result.success === false) {
                    reject(new Error(result.message || 'Failed to save evaluation settings'));
                    return;
                }
                resolve(payload);
            },
            (error) => reject(error instanceof Error ? error : new Error(String(error))),
        );
    });

export const useEvaluationNormalizationAutoSave = ({
    usecase,
    normalization,
    enabled = true,
    role,
    onSaved,
}) => {
    const { updateUsecase } = useContext(GlobalContext);

    const [autosaveError, setAutosaveError] = useState(null);
    const lastSavedRef = useRef(evaluationNormalizationFromUsecase(null));
    const saveTimerRef = useRef(null);
    const persistInFlightRef = useRef(false);

    const isViewer = role === 'Viewer';

    const latestRef = useRef({ normalization, onSaved, updateUsecase, usecase });
    latestRef.current = { normalization, onSaved, updateUsecase, usecase };

    useEffect(() => {
        if (!enabled) return;
        lastSavedRef.current = evaluationNormalizationFromUsecase(usecase);
        setAutosaveError(null);
        if (saveTimerRef.current) {
            clearTimeout(saveTimerRef.current);
            saveTimerRef.current = null;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enabled, usecase?._id]);

    const persistSave = useCallback(async () => {
        const {
            normalization: latestNormalization,
            onSaved: latestOnSaved,
            updateUsecase: latestUpdateUsecase,
            usecase: latestUsecase,
        } = latestRef.current;

        if (!latestUsecase?._id) {
            throw new Error('Task not loaded yet');
        }
        if (evaluationNormalizationEqual(latestNormalization, lastSavedRef.current)) {
            return { savedSomething: false };
        }
        if (persistInFlightRef.current) {
            return { savedSomething: false };
        }
        persistInFlightRef.current = true;
        try {
            const savedFields = await saveEvaluationNormalization({
                usecase: latestUsecase,
                normalization: latestNormalization,
            });
            lastSavedRef.current = { ...latestNormalization };
            if (typeof latestUpdateUsecase === 'function') {
                latestUpdateUsecase(latestUsecase._id, savedFields);
            }
            if (typeof latestOnSaved === 'function') {
                latestOnSaved({ llm_usecase_id: latestUsecase._id, ...savedFields });
            }
            return { savedSomething: true };
        } finally {
            persistInFlightRef.current = false;
        }
    }, []);

    const isDirty = !evaluationNormalizationEqual(normalization, lastSavedRef.current);

    useEffect(() => {
        if (!enabled || isViewer) return undefined;
        if (!usecase?._id || !usecase?.client_id) return undefined;
        if (!isDirty) return undefined;

        if (saveTimerRef.current) clearTimeout(saveTimerRef.current);

        let cancelled = false;
        saveTimerRef.current = setTimeout(async () => {
            saveTimerRef.current = null;
            try {
                setAutosaveError(null);
                await persistSave();
            } catch (e) {
                if (!cancelled) {
                    setAutosaveError(e?.message || 'Failed to save evaluation settings');
                }
            }
        }, AUTOSAVE_DEBOUNCE_MS);

        return () => {
            cancelled = true;
            if (saveTimerRef.current) {
                clearTimeout(saveTimerRef.current);
            }
        };
    }, [
        normalization?.normalizeCasing,
        normalization?.stripWhitespace,
        normalization?.fixJsonFormat,
        enabled,
        isViewer,
        usecase?._id,
        usecase?.client_id,
        isDirty,
        persistSave,
    ]);

    // Persist a pending debounce when the tab unmounts.
    useEffect(() => {
        return () => {
            if (saveTimerRef.current == null) return;
            clearTimeout(saveTimerRef.current);
            saveTimerRef.current = null;
            persistSave().catch(() => {});
        };
    }, [persistSave]);

    const flushSave = useCallback(async () => {
        if (saveTimerRef.current) {
            clearTimeout(saveTimerRef.current);
            saveTimerRef.current = null;
        }
        try {
            setAutosaveError(null);
            return await persistSave();
        } catch (e) {
            setAutosaveError(e?.message || 'Failed to save evaluation settings');
            throw e;
        }
    }, [persistSave]);

    const clearAutosaveError = useCallback(() => setAutosaveError(null), []);

    return { autosaveError, isDirty, flushSave, clearAutosaveError };
};
